import { useEffect, useState } from 'react';
import { ExerciseConfigComponent } from '../elements/ExerciseConfigComponent';
import { ErrorComponent } from '@/components/ErrorComponent';
import { Box, Button, Divider, IconButton, Modal, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Add, Close, MenuBookOutlined } from '@mui/icons-material';
import { modalStyle } from '@/components/modalStyle';
import { ExerciseDTO, DeckDTO, ExerciseConfig } from '../../../generated';
import { objectHasId } from '@/utils/create-mode-helper';
import { ImageFromServer } from '@/components/images/ImageFromServer';
import { validateDeckExerciseConfig } from '@/validation/exercise';
import { useUpdateExerciseInDeck } from '@/hooks/deck-mutation.hooks';

export interface UpdateDeckExerciseModalProps {
  exercise?: ExerciseDTO;
  deck: DeckDTO;
  onClose?: () => void;
  show: boolean;
}

export const UpdateDeckExerciseModal = (props: UpdateDeckExerciseModalProps) => {
  const { deck, show, exercise, onClose } = props;
  const [config, setConfig] = useState<ExerciseConfig | undefined>(exercise?.config);
  const [showNote, setShowNote] = useState(Boolean(exercise?.config.note));
  const [validationErrors, setValidationErrors] = useState<Array<string>>([]);
  const isEditMode = exercise ? objectHasId(exercise) : false;
  const updateDeckExerciseMutation = useUpdateExerciseInDeck();

  useEffect(() => {
    setConfig(exercise?.config);
    setShowNote(Boolean(exercise?.config.note));
  }, [exercise]);

  return exercise ? (
    <Modal
      sx={{ marginTop: 5 }}
      open={show}
      onClose={() => {
        setConfig(undefined);
        if (onClose) onClose();
      }}
    >
      <Box sx={{ ...modalStyle }}>
        <Box sx={{ my: 3 }}>
          <Stack direction={'row'}>
            <Typography variant="h5">{`${exercise.name}`} </Typography>
            <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'flex-end' }}>
              <Close
                onClick={() => {
                  if (props.onClose) props.onClose();
                  setConfig(undefined);
                }}
              />
            </Box>
          </Stack>
        </Box>
        <Stack>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 3 }}>
            <ImageFromServer id={exercise.imageId} alt={'Exercise Image'} thumbnail={false} style={{ maxWidth: 225, maxHeight: 225 }} />
          </Box>
          {config && (
            <ExerciseConfigComponent
              exercise={exercise}
              exerciseConfig={config}
              updateExerciseConfig={(cfg) => {
                setConfig(cfg);
              }}
            />
          )}
          {!showNote && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
              <Tooltip title="Add note">
                <IconButton onClick={() => setShowNote(true)} aria-label="Add note">
                  <Box sx={{ position: 'relative', display: 'flex' }}>
                    <MenuBookOutlined />
                    <Add sx={{ position: 'absolute', right: -8, bottom: -6, fontSize: 16 }} />
                  </Box>
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {showNote && (
            <TextField
              label="Note"
              value={config?.note ?? ''}
              onChange={(event) => {
                if (config !== undefined) setConfig({ ...config, note: event.target.value });
              }}
              multiline
              minRows={2}
              fullWidth
              sx={{ mb: 1, mt: 3 }}
            />
          )}
        </Stack>
        <Divider sx={{ my: 2 }} />

        <ErrorComponent errors={validationErrors} />

        <Box sx={{ m: 1 }}>
          <Button
            onClick={async () => {
              if (config !== undefined) {
                const errors = validateDeckExerciseConfig(config);

                setValidationErrors(errors);
                if (errors.length > 0) return;
                await updateDeckExerciseMutation.mutateAsync({ deckId: deck.id, exerciseConfig: config, exerciseId: exercise.id });
              }

              setConfig(undefined);
              if (props.onClose) props.onClose();
            }}
          >
            {isEditMode ? 'Save' : 'Create'}
          </Button>
          <Button
            onClick={() => {
              setConfig(undefined);
              if (onClose) onClose();
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  ) : (
    <></>
  );
};
